/* eslint-disable */
import type { DeviceInfo, OTPInfo, PasskeyOptions, PasswordInfo } from "../access/sync_values.pb";
import type { Account } from "../accountstatement/account_statement.pb";
import type { UiFamilyAccount } from "../cash/aegis/sync_values/UiFamilyAccount.pb";
import type { BusinessProfileData } from "../cash/cashbusinessaccounts/sync_values.pb";
import type { AvailableReactions } from "../cash/cashreactions/api/v1/service_admin.pb";
import type { UiEfileInfo } from "../cash/cashtaxgateway/sync_values/UiEfileInfo.pb";
import type { Cats } from "../cash/cats/cats.pb";
import type { CryptocurrencyProfile } from "../cash/cryptocurrency/CryptocurrencyProfile.pb";
import type { SponsorshipCryptoAuthorization } from "../cash/cryptocurrency/SponsorshipCryptoAuthorization.pb";
import type { Wallet } from "../cash/cryptocurrency/Wallet.pb";
import type { DataPrivacySettings } from "../cash/dataprivacy/settings/data_privacy_settings.pb";
import type { Favorite } from "../cash/favorites/favorite.pb";
import type { GroupExpense } from "../cash/groups/expense.pb";
import type { Group } from "../cash/groups/group.pb";
import type { TradingState, TradingStateDividendSetting } from "../cash/investcore/trading/syncvalues/types.pb";
import type { Lions } from "../cash/lions/lions.pb";
import type { OfferCustomerPreference } from "../cash/offerdex/sync/OfferCustomerPreference.pb";
import type { OverdraftStatus } from "../cash/overdraft/OverdraftStatusProto.pb";
import type { OverdraftUsage } from "../cash/overdraft/OverdraftUsageProto.pb";
import type { AllocationDistribution } from "../cash/paychecks/AllocationDistribution.pb";
import type { UiConfiguration } from "../cash/paychecks/UiConfiguration.pb";
import type { UiState } from "../cash/paychecks/UiState.pb";
import type { PortfolioState } from "../cash/portfolios/syncvalues/types.pb";
import type { ProfileDetails } from "../cash/profiles/ProfileDetails.pb";
import type { Rabbits } from "../cash/rabbits/rabbits.pb";
import type { InternationalPaymentsCountrySelectionSyncValue } from "../cash/remittances/syncvalue/v1/international_payments_country_selection_sync_value.pb";
import type { SavingsConfig } from "../cash/savings/SavingsConfig.pb";
import type { SavingsFolder } from "../cash/savings/SavingsFolder.pb";
import type { SavingsHome } from "../cash/savings/SavingsHome.pb";
import type { DomainType } from "../cash/syncentity/DomainType.pb";
import type { UiTaxUpgrade } from "../cash/taxfrontdoor/sync_values/UiTaxUpgrade.pb";
import type { Tigers } from "../cash/tigers/tigers.pb";
import type { SyncTrustedContact } from "../cash/trustedcontact/trusted_contact.pb";
import type { Color } from "../cash/ui/Color.pb";
import type { Image } from "../cash/ui/Image.pb";
import type { WiresAccountInfo } from "../cash/wires/WiresAccountInfo.pb";
import type { CheckDepositProfile } from "../checkmate/profile/check_deposit_profile.pb";
import type { CheckDepositEligibility } from "../checkmate/sync/check_deposit_eligibility.pb";
import type { UiCheckDeposit } from "../checkmate/sync/ui_check_deposit.pb";
import type { Money } from "../common/currency.pb";
import type { CryptoPayrollPreference } from "../crypto-invest-flow/crypto_payroll_preference.pb";
import type { DocumentCategoryEntity, DocumentEntity } from "../document/document.pb";
import type { GiftCard } from "../giftly/gift_card.pb";
import type {
  NotificationSettingsMessageConfig,
  NotificationSettingsOption,
  NotificationsSettings,
} from "../investing/notifications/settings.pb";
import type {
  BorrowAppletBulletinsTile,
  BorrowAppletCreditLimitAndBorrowButtonTile,
  BorrowAppletLoanHistoryTile,
  BorrowAppletPaymentTimelineTile,
  BorrowConfig,
  GlobalBorrowConfig,
  LendingInfo,
} from "../lending/sync_values.pb";
import type { LoyaltyProfile } from "../loyalizer/loyalty_profile.pb";
import type { PaperCashDepositEligibility } from "../papermate/paper_cash_deposit_eligibility.pb";
import type { ScheduledTransactionPreference } from "../repeatedly/common.pb";
import type { BankingTab } from "../unicorn/banking_tab.pb";
import type { WiresEligibilityState } from "../wiremate/wires_eligibility_state.pb";
import type { Instrument } from "./api.pb";
import type { InstrumentLinkingOption, ProfileAlias } from "./common.pb";
import type { InvestmentEntityStatus, InvestmentEntityType } from "./investing/resources.pb";
import type { CreditLine, Loan, LoanTransaction } from "./lending.pb";
import type { PaymentConversationData } from "./payment_conversation.pb";
import type { ScheduledReloadData } from "./schedule_data.pb";
import type { UiAccess } from "./ui/access.pb";
import type { UiAddress } from "./ui/address.pb";
import type { UiAppMesssages } from "./ui/app_messages.pb";
import type { UiAppLock } from "./ui/applock.pb";
import type { BalanceSnapshot } from "./ui/balance_snapshot.pb";
import type { UiCashLimits } from "./ui/cash_limits.pb";
import type { UiCoreCustomer } from "./ui/core_customer.pb";
import type { UiCryptoCurrency } from "./ui/cryptocurrency.pb";
import type { UiCustomer } from "./ui/customer.pb";
import type { UiDda } from "./ui/dda.pb";
import type { UiExchangeData } from "./ui/exchange_data.pb";
import type { UiExperiments } from "./ui/experiments.pb";
import type { IdentityHubState } from "./ui/identity_hub_state.pb";
import type { UiIdvState } from "./ui/idv_state.pb";
import type { UiInvestingAutomation } from "./ui/investing_automation.pb";
import type { UiInvitation } from "./ui/invitation.pb";
import type { UiIssuedCard } from "./ui/issued_card.pb";
import type { UiItemizedReceipt } from "./ui/itemized_receipt.pb";
import type { UiJurisdictionConfig } from "./ui/jurisdiction_config.pb";
import type { LimitsPageletInlineMessage } from "./ui/limits_pagelet_inline_message.pb";
import type { UiLoyaltyAccount } from "./ui/loyalty_account.pb";
import type { UiLoyaltyProgram } from "./ui/loyalty_program.pb";
import type { UiMarketing } from "./ui/marketing.pb";
import type { UiNotificationPreference } from "./ui/notification_preference.pb";
import type { UiP2pSettings } from "./ui/p2p_settings.pb";
import type { UiPayment } from "./ui/payment.pb";
import type { UiPaymentSchedule } from "./ui/payment_schedule.pb";
import type { UiPublicProfile } from "./ui/public_profile.pb";
import type { RewardSelection } from "./ui/reward_selection.pb";
import type { UiScheduledPayments } from "./ui/scheduled_payments.pb";
import type { UiStatusAndLimits } from "./ui/status_and_limits.pb";
import type { UiTax } from "./ui/tax.pb";

export enum SyncEntityType {
  DO_NOT_USE_SYNC_ENTITY_TYPE = "DO_NOT_USE_SYNC_ENTITY_TYPE",
  /** PAYMENT - A Cash App payment. */
  PAYMENT = "PAYMENT",
  /** CUSTOMER - A Cash App customer. */
  CUSTOMER = "CUSTOMER",
  /** TRANSFER - A transfer of funds to add cash or cash out from a balance. */
  TRANSFER = "TRANSFER",
  /** TRANSACTION - A transaction made with a balance, such as a payment with a virtual or physical card. */
  TRANSACTION = "TRANSACTION",
  /** MERCHANT - A merchant who initiated the transaction. */
  MERCHANT = "MERCHANT",
  /**
   * SCHEDULED_PAYMENT - A future scheduled payment.
   *
   * @deprecated
   */
  SCHEDULED_PAYMENT = "SCHEDULED_PAYMENT",
  /** LOYALTY_ACCOUNT - A loyalty account (the customer’s loyalty earnings at a merchant). */
  LOYALTY_ACCOUNT = "LOYALTY_ACCOUNT",
  /**
   * LOYALTY_MERCHANT - A Square Loyalty merchant.
   *
   * @deprecated
   */
  LOYALTY_MERCHANT = "LOYALTY_MERCHANT",
  /** REWARD_SELECTION - Detailed information about a reward/Boost selection. */
  REWARD_SELECTION = "REWARD_SELECTION",
  /** LOYALTY_PROGRAM - A Square Loyalty program */
  LOYALTY_PROGRAM = "LOYALTY_PROGRAM",
  /** INVESTMENT_HOLDING - A single investment holding for a customer. */
  INVESTMENT_HOLDING = "INVESTMENT_HOLDING",
  /** INVESTMENT_ENTITY - A Investment entity is a asset that can be traded on the Cash Investment platform . */
  INVESTMENT_ENTITY = "INVESTMENT_ENTITY",
  /** RECEIPT_ENTITY - A Receipt entity represents a receipt for a transaction. */
  RECEIPT_ENTITY = "RECEIPT_ENTITY",
  /** SYNC_VALUE - A SyncEntity to store a singleton or small number of values, used to construct the app profile. */
  SYNC_VALUE = "SYNC_VALUE",
  /** PAYMENT_SCHEDULE - A schedule that defines future and/or recurring payments. */
  PAYMENT_SCHEDULE = "PAYMENT_SCHEDULE",
  /** CREDIT_LINE - A line of credit for a customer. */
  CREDIT_LINE = "CREDIT_LINE",
  /** LOAN - A loan drawn from a line of credit. */
  LOAN = "LOAN",
  /** LOAN_TRANSACTION - A transaction on a loan such as a scheduled payment, loan payment, late fee, or interest. */
  LOAN_TRANSACTION = "LOAN_TRANSACTION",
  /** LOAN_ACTIVITY - An event relating to a loan that appears in Activity. For example, a new loan or payment. */
  LOAN_ACTIVITY = "LOAN_ACTIVITY",
  /** INVITATION - An invitation to use Cash App. */
  INVITATION = "INVITATION",
  /** RECURRING_PREFERENCES - User settings about recurring bitcoin and equity buys. */
  RECURRING_PREFERENCES = "RECURRING_PREFERENCES",
  /** INVESTMENT_CATEGORY - Category of investment entities. */
  INVESTMENT_CATEGORY = "INVESTMENT_CATEGORY",
  /** INVESTMENT_FILTER_GROUP - Grouping of investment categories with UI configuration. */
  INVESTMENT_FILTER_GROUP = "INVESTMENT_FILTER_GROUP",
  /** INVESTMENT_ENTITY_RANKING - An ordering of how investment entities should be presented (e.g., in Search). */
  INVESTMENT_ENTITY_RANKING = "INVESTMENT_ENTITY_RANKING",
  /** INVESTMENT_INCENTIVE - An incentive for a customer from investing */
  INVESTMENT_INCENTIVE = "INVESTMENT_INCENTIVE",
  /** CHECK_DEPOSIT_RECEIPT_ENTITY - Check Deposit Receipt */
  CHECK_DEPOSIT_RECEIPT_ENTITY = "CHECK_DEPOSIT_RECEIPT_ENTITY",
  /** INVEST_DEFAULT_NOTIFICATION_SETTINGS - Invest notification default settings. */
  INVEST_DEFAULT_NOTIFICATION_SETTINGS = "INVEST_DEFAULT_NOTIFICATION_SETTINGS",
  /** CASH_HOUSE_SCREEN - See http://go/treehouse */
  CASH_HOUSE_SCREEN = "CASH_HOUSE_SCREEN",
  /** SPONSORED_ACCOUNT - A domain representation of the sponsored account life cycle. See http://go/pg13designdoc * */
  SPONSORED_ACCOUNT = "SPONSORED_ACCOUNT",
  /** DOCUMENT_CATEGORY_ENTITY - Document category entity. */
  DOCUMENT_CATEGORY_ENTITY = "DOCUMENT_CATEGORY_ENTITY",
  /** DOCUMENT_ENTITY - A Document entity. */
  DOCUMENT_ENTITY = "DOCUMENT_ENTITY",
  /**
   * BUSINESS_GRANT - A PwC Business Grant entity. Represents permission given to a business to pull payments or
   * view a customer's data.
   */
  BUSINESS_GRANT = "BUSINESS_GRANT",
  /** GIFT_CARD - A Gift Card entity */
  GIFT_CARD = "GIFT_CARD",
  /** NEW_DEVICE_LOGIN - Used for entries in activity feed for new device logins */
  NEW_DEVICE_LOGIN = "NEW_DEVICE_LOGIN",
  /** CRYPTO_PAYROLL_PREFERENCE - Used for crypto payroll configurations */
  CRYPTO_PAYROLL_PREFERENCE = "CRYPTO_PAYROLL_PREFERENCE",
  /** ACCOUNT_STATEMENT - A banking statement entity */
  ACCOUNT_STATEMENT = "ACCOUNT_STATEMENT",
  /** GROUP - Cash group entity */
  GROUP = "GROUP",
  /** GROUP_EXPENSE - Cash group expense entity */
  GROUP_EXPENSE = "GROUP_EXPENSE",
  /** INVEST_DEFAULT_DIVIDEND_SETTING - Invest dividend reinvestment plan default setting. */
  INVEST_DEFAULT_DIVIDEND_SETTING = "INVEST_DEFAULT_DIVIDEND_SETTING",
  /** COUPON - Used for Coupon activities */
  COUPON = "COUPON",
  /** REACTIONS - List of emojis a customer can react with */
  REACTIONS = "REACTIONS",
  /** SPONSORSHIP_FEATURE_INVITATION - Sponsorship feature invitation entity * */
  SPONSORSHIP_FEATURE_INVITATION = "SPONSORSHIP_FEATURE_INVITATION",
  /** COUNTRY_SCREEN_CONFIG - Entry point for cross-border and remittance payments */
  COUNTRY_SCREEN_CONFIG = "COUNTRY_SCREEN_CONFIG",
}

export enum SyncValueType {
  DO_NOT_USE_SYNC_VALUE_TYPE = "DO_NOT_USE_SYNC_VALUE_TYPE",
  INSTRUMENT = "INSTRUMENT",
  EQUITIES_PURCHASE_ATM_PICKER = "EQUITIES_PURCHASE_ATM_PICKER",
  BITCOIN_PURCHASE_ATM_PICKER = "BITCOIN_PURCHASE_ATM_PICKER",
  BALANCE_SNAPSHOT = "BALANCE_SNAPSHOT",
  DDA = "DDA",
  ACCESS = "ACCESS",
  ADDRESS = "ADDRESS",
  STATUS_AND_LIMITS = "STATUS_AND_LIMITS",
  APP_MESSAGES = "APP_MESSAGES",
  CORE_CUSTOMER = "CORE_CUSTOMER",
  EXPERIMENTS = "EXPERIMENTS",
  ISSUED_CARD = "ISSUED_CARD",
  MARKETING = "MARKETING",
  P2P_SETTINGS = "P2P_SETTINGS",
  SCHEDULED_PAYMENTS = "SCHEDULED_PAYMENTS",
  TAX = "TAX",
  PUBLIC_PROFILE = "PUBLIC_PROFILE",
  PROFILE_ALIAS = "PROFILE_ALIAS",
  LOYALTY_PROFILE = "LOYALTY_PROFILE",
  CHECK_DEPOSIT_PROFILE = "CHECK_DEPOSIT_PROFILE",
  INVESTMENT_NOTIFICATION_SETTINGS = "INVESTMENT_NOTIFICATION_SETTINGS",
  PROFILE_DETAILS = "PROFILE_DETAILS",
  CRYPTO_WALLET = "CRYPTO_WALLET",
  BANKING_TAB = "BANKING_TAB",
  JURISDICTION_CONFIG = "JURISDICTION_CONFIG",
  CRYPTOCURRENCY = "CRYPTOCURRENCY",
  EXCHANGE_DATA = "EXCHANGE_DATA",
  NOTIFICATION_PREFERENCE = "NOTIFICATION_PREFERENCE",
  DATA_PRIVACY_SETTINGS = "DATA_PRIVACY_SETTINGS",
  PASSWORD_INFO = "PASSWORD_INFO",
  OTP_INFO = "OTP_INFO",
  FAMILY_ACCOUNT = "FAMILY_ACCOUNT",
  INVESTING_AUTOMATION = "INVESTING_AUTOMATION",
  LENDING_INFO = "LENDING_INFO",
  TRUSTED_CONTACT = "TRUSTED_CONTACT",
  IDV_STATE = "IDV_STATE",
  CRYPTOCURRENCY_PROFILE = "CRYPTOCURRENCY_PROFILE",
  INVEST_AUTOMATOR_NOTIFICATION_SETTINGS = "INVEST_AUTOMATOR_NOTIFICATION_SETTINGS",
  FAVORITE = "FAVORITE",
  SAVINGS_CONFIG = "SAVINGS_CONFIG",
  SAVINGS_HOME = "SAVINGS_HOME",
  INVEST_PORTFOLIO_STATE = "INVEST_PORTFOLIO_STATE",
  INVEST_TRADING_STATE = "INVEST_TRADING_STATE",
  SAVINGS_FOLDER = "SAVINGS_FOLDER",
  TIGERS = "TIGERS",
  WIRES = "WIRES",
  TAXUPG = "TAXUPG",
  TAXEFI = "TAXEFI",
  CASH_LIMITS = "CASH_LIMITS",
  LIONS = "LIONS",
  DEVICE = "DEVICE",
  LIMITS_PAGELET_INLINE_MESSAGE = "LIMITS_PAGELET_INLINE_MESSAGE",
  SCHEMA_VERSIONS = "SCHEMA_VERSIONS",
  CATS = "CATS",
  INSTRUMENT_LINKING_OPTION = "INSTRUMENT_LINKING_OPTION",
  RABBITS = "RABBITS",
  OVERDRAFT_STATUS = "OVERDRAFT_STATUS",
  OVERDRAFT_USAGE = "OVERDRAFT_USAGE",
  INSTRUMENT_LINKING_OPTIONS = "INSTRUMENT_LINKING_OPTIONS",
  IDENTITY_HUB_STATE = "IDENTITY_HUB_STATE",
  APP_LOCK = "APP_LOCK",
  SPONSORSHIP_CRYPTO_AUTHORIZATION = "SPONSORSHIP_CRYPTO_AUTHORIZATION",
  OFFER_CUSTOMER_PREFERENCE = "OFFER_CUSTOMER_PREFERENCE",
  PASSKEY_OPTIONS = "PASSKEY_OPTIONS",
  PAYCHECKS_ALLOCATION_DISTRIBUTION = "PAYCHECKS_ALLOCATION_DISTRIBUTION",
  PAYCHECKS_UI_CONFIGURATION = "PAYCHECKS_UI_CONFIGURATION",
  PAYCHECKS_UI_STATE = "PAYCHECKS_UI_STATE",
  PAPER_CASH_DEPOSIT_ELIGIBILITY = "PAPER_CASH_DEPOSIT_ELIGIBILITY",
  CHECK_DEPOSIT_ELIGIBILITY = "CHECK_DEPOSIT_ELIGIBILITY",
  BORROW_CONFIG = "BORROW_CONFIG",
  GLOBAL_BORROW_CONFIG = "GLOBAL_BORROW_CONFIG",
  BORROW_APPLET_CREDIT_LIMIT_BORROW_TILE = "BORROW_APPLET_CREDIT_LIMIT_BORROW_TILE",
  C4B_PROFILE = "C4B_PROFILE",
  REACTIONS_AVAILABLE = "REACTIONS_AVAILABLE",
  BORROW_APPLET_PAYMENT_TIMELINE_TILE = "BORROW_APPLET_PAYMENT_TIMELINE_TILE",
  BORROW_APPLET_BULLETINS_TILE = "BORROW_APPLET_BULLETINS_TILE",
  INTERNATIONAL_PAYMENT_CONFIG = "INTERNATIONAL_PAYMENT_CONFIG",
  BORROW_APPLET_LOAN_HISTORY_TILE = "BORROW_APPLET_LOAN_HISTORY_TILE",
  /** ADD_CASH_ATM_PICKER_CONFIG - next id: 89 */
  ADD_CASH_ATM_PICKER_CONFIG = "ADD_CASH_ATM_PICKER_CONFIG",
}

export interface SyncEntity {
  type?: SyncEntityType;
  entity_id?: string;
  /**
   * Strictly increasing version of the entity. Used to defend against race conditions.
   * Clients (apps, ClientSync) should keep the latest version.
   *
   * For a more comprehensive explanation of optimistic versioning see
   * https://pages.sqcorp.co/pages/XP/api-styleguide/release/browse/API_Style_Guide.html#optimistic-concurrency
   */
  version?: number;
  /**
   * UNIX timestamp in milliseconds relevant to this sync entity's membership in its topic. For
   * payments, this is the time the payment is created. For counterparties, this could be the time
   * of the earliest interaction with the counterparty.
   *
   * This is used for:
   *
   *  * Sorting sync entities by chronological order. On the web we page sync entities by date.
   *  * Hinting which entities should be synced to the device. Everything is synced as of 2019-06;
   *    this is going to be too much data for some users so we’ll eventually limit to some number
   *    of days.
   *  * This field is required when SyncEntity.payment is populated. Otherwise it is optional.
   */
  display_date?: number;
  /**
   * If true, this SyncEntity has not completed rendering, so there is no meaningful data available
   * to read from this object beyond only the type and entity_id. This field can be true only if it
   * was returned from ClientSync to Franklin, was originally added as a SyncEntityReference
   * through an AddSyncEntityRequest to ClientSync, and the SyncTopic and entity_id it was
   * referencing did not exist in the ClientSync database.
   * That means this entity relies on Franklin to render from its own database, if it exists.
   * This SyncEntity should never be returned to the client if this field is set to true.
   */
  is_unrendered?: boolean;
  /**
   * If true, the sync entity will be deleted from clients db. This is a replacement to sending
   * ambiguous null sync entities for deletion. Effective since 3.39.
   */
  deleted?: boolean;
  /**
   * Describes the domain type of the sync entity and what the sync entity's intended purpose is.
   * This can be used to make targeted queries and filter based on domain type.
   * This is different than SyncEntity.Type which currently describes the sync entity schema not the
   * actual data content type. For example, SyncEntity.Type PAYMENT today does not actually always
   * represent a payment, it just uses the payment schema
   */
  domain_type?: DomainType;
  /**
   * UNIX timestamp in milliseconds representing the time that ClientSync persisted this version
   * of the entity. Entity writers should not set this field: ClientSync will overwrite it. This
   * field is for operational use only: it should not be used for any business logic.
   */
  client_sync_stored_at?: number;
  /** Type-specific schema version, used by Fetch-as-Needed to get updates */
  schema_version?: number;
  /**
   * Version of the external construct represented by this SyncEntity.
   * This is required if badging_info is populated.
   */
  external_version?: number;
  /** Badging state and version. Services writing to ClientSync should not set this. */
  badging_info?: BadgingInfo;
  /**
   * At most one of the following fields will ever be populated at a time, as indicated by the
   * SyncEntityType. When the server wants the client to delete the entity, all the fields
   * below will be null and only the entity_id will be populated.
   *
   * (Ideally, this would be a real oneof, and that change would be on-the-wire compatible.
   * However, it would cause compile-time failures for existing usage.)
   * >>>> BEGIN virtual oneof for SyncEntity
   */
  payment?: SyncPayment;
  customer?: SyncCustomer;
  loyalty_account?: SyncLoyaltyAccount;
  reward_selection?: SyncRewardSelection;
  loyalty_program?: SyncLoyaltyProgram;
  investment_holding?: SyncInvestmentHolding;
  investment_entity?: SyncInvestmentEntity;
  receipt_entity?: SyncReceiptEntity;
  sync_value?: SyncValue;
  payment_schedule?: SyncPaymentSchedule;
  credit_line?: CreditLine;
  loan?: Loan;
  loan_transaction?: LoanTransaction;
  invitation?: SyncInvitation;
  recurring_preference?: SyncRecurringPreference;
  investment_category?: SyncInvestmentCategory;
  investment_filter_group?: SyncInvestmentFilterGroup;
  investment_entity_ranking?: SyncInvestmentEntityRanking;
  /** @deprecated */
  investment_incentive?: SyncInvestmentIncentive;
  check_deposit_receipt_entity?: SyncCheckDepositReceiptEntity;
  encrypted_sync_entity?: EncryptedSyncEntity;
  invest_default_notification_settings?: InvestDefaultNotificationSettings;
  cash_house_screen?: CashHouseScreen;
  document_category_entity?: DocumentCategoryEntity;
  document_entity?: DocumentEntity;
  business_grant?: SyncBusinessGrant;
  gift_card?: SyncGiftCardEntity;
  crypto_payroll_preference?: SyncCryptoPayrollPreference;
  account_statement?: SyncAccountStatementEntity;
  group?: SyncGroup;
  group_expense?: SyncGroupExpense;
  invest_default_dividend_setting?: TradingStateDividendSetting;
  /**
   * Don't forget to add a new type to `SyncEntityType` as well.
   * <<<< END virtual oneof for SyncEntity (DO NOT add generic, top-level sync entity fields here)
   */
  reactions?: SyncReactions;
}

export interface SyncGroup {
  group?: Group;
}

export interface SyncGroupExpense {
  expense?: GroupExpense;
}

export interface SyncReactions {
  emoji?: string[];
}

export interface SyncAccountStatementEntity {
  account?: Account;
}

export interface SyncGiftCardEntity {
  gift_card?: GiftCard;
}

export interface SyncPayment {
  payment?: UiPayment;
  /** @deprecated */
  conversation?: PaymentConversationData;
  is_badged?: boolean;
}

export interface SyncBusinessGrant {
  /** The ID of this business grant. */
  id?: string;
  /** The merchant that was used as the customer facing brand when this grant was created. */
  merchant_id?: string;
  /** Date last updated in milliseconds since UNIX epoch. */
  updated_at?: number;
  /** Date created in milliseconds since UNIX epoch. */
  created_at?: number;
  /**
   * Date this grant can no longer be used in
   * milliseconds since the UNIX epoch.
   */
  expires_at?: number;
  /**
   * ID of the API client that can use this grant. In
   * the future, it’s possible that one Merchantein
   * parent merchant could have grants spread across
   * multiple API clients.
   *
   * If we wanted to handle this elegantly, we could
   * group the grants by merchant_id on the linked
   * businesses page, then display the client that
   * is associated with each grant when a customer
   * wants to revoke a grant, so that they can pick and
   * choose where they want to revoke access from.
   */
  client_id?: string;
  /**
   * The type of action that can be taken on the
   * the customer with the synced grant using the
   * public Cash App API.
   */
  action_type?: SyncBusinessGrantActionType;
  /**
   * If two synced grants for the same merchant and
   * action type are present, this field indicates
   * which third-party account is associated with the
   * grant.
   *
   * For example, let’s say someone has two Amazon
   * accounts - one for them and one for their kid.
   * If they linked their Cash App account to both,
   * this field would be “parent@example.com” for one
   * grant and “kid@example.com” for the other.
   *
   * This should be displayed in the UI so that
   * customers can differentiate grants.
   */
  account_reference_id?: string;
}

/** Represents the type of action. */
export enum SyncBusinessGrantActionType {
  DO_NOT_USE_ACTION_TYPE = "DO_NOT_USE_ACTION_TYPE",
  /** ONE_TIME_PAYMENT - Indicates that this grant can be used for a one time payment. */
  ONE_TIME_PAYMENT = "ONE_TIME_PAYMENT",
  /** ON_FILE_PAYMENT - Indicates that this grant can be used for an on file payment. */
  ON_FILE_PAYMENT = "ON_FILE_PAYMENT",
  /** UNLINKED_REFUND - Indicates that this grant can be used for an unlinked refund. */
  UNLINKED_REFUND = "UNLINKED_REFUND",
  /** LINK_ACCOUNT - Indicates that this grant can be used to link an account. */
  LINK_ACCOUNT = "LINK_ACCOUNT",
  /** RECURRING_DEPOSITS - Indicates that this grant can be used for recurring deposits. */
  RECURRING_DEPOSITS = "RECURRING_DEPOSITS",
  /** ON_FILE_DEPOSIT - Indicates that this grant can be used for on file deposit. */
  ON_FILE_DEPOSIT = "ON_FILE_DEPOSIT",
}

export interface SyncPaymentSchedule {
  payment_schedule?: UiPaymentSchedule;
}

export interface SyncCustomer {
  customer?: UiCustomer;
}

export interface SyncLoyaltyAccount {
  loyalty_account?: UiLoyaltyAccount;
}

export interface SyncLoyaltyProgram {
  loyalty_program?: UiLoyaltyProgram;
}

export interface SyncRewardSelection {
  reward_selection?: RewardSelection;
}

/** A customer's holdings for an individual investment entity. */
export interface SyncInvestmentHolding {
  /** Identifier of investment entity. */
  investment_entity_token?: string;
  /**
   * Units held by customer; decimal value is rounded by server and sent as string.
   * E.g.: "0.00022"
   */
  units?: string;
  /** Total amount invested in this investment entity. */
  active_invested_amount?: Money;
  /** State of this investment holding by the customer. e.g.: OWNED, FOLLOWED. */
  state?: SyncInvestmentHoldingInvestmentHoldingState;
  /** The customer's current scheduled buy preference. */
  scheduled_buy_data?: ScheduledReloadData;
  /** parameters for daily gain computation */
  daily_gain_params?: SyncInvestmentHoldingDailyGainParams;
  /** Average cost per share for this investment entity. */
  average_cost?: Money;
}

/** Available states for an investment holding to be returned to a customer. */
export enum SyncInvestmentHoldingInvestmentHoldingState {
  DO_NOT_USE_INVESTMENT_HOLDING_STATE = "DO_NOT_USE_INVESTMENT_HOLDING_STATE",
  /** OWNED - A quantity of this investment entity is owned by this customer. */
  OWNED = "OWNED",
  /** FOLLOWED - Customer has marked this investment entity to be followed and unowned. */
  FOLLOWED = "FOLLOWED",
  /** UNFOLLOWED - Indicates an investment entity is unfollowed and unowned. */
  UNFOLLOWED = "UNFOLLOWED",
}

export interface SyncInvestmentHoldingDailyGainParams {
  /** Time of market close on day of last trade. */
  valid_until_seconds?: number;
  /** Units already held by customer before any trades on the day trading occurred. */
  units_at_market_open?: string;
  /** Sum of all buy amounts minus all sell amounts. */
  day_cash_value_change?: Money;
}

/**
 * A customer's incentive for purchasing stocks.
 *
 * @deprecated
 */
export interface SyncInvestmentIncentive {
  /** Identifier of the incentive. */
  investment_incentive_token?: string;
  /** Total amount invested in this investment entity. */
  offer_amount?: Money;
  /** State of this incentive for the customer. e.g.: UNCLAIMED, INELIGIBLE. */
  state?: SyncInvestmentIncentiveIncentiveState;
  /** web address of the icon for the incentive */
  incentive_icon?: Image;
  /** Incentive text to be displayed to the user */
  text?: string;
  /** Timestamp when the incentive expires (epoch seconds, UTC) */
  incentive_expiration_timestamp?: number;
}

/** Available states for an investment holding to be returned to a customer. */
export enum SyncInvestmentIncentiveIncentiveState {
  DO_NOT_USE_INCENTIVE_STATE = "DO_NOT_USE_INCENTIVE_STATE",
  /** UNCLAIMED - The incentive is unclaimed by the customer */
  UNCLAIMED = "UNCLAIMED",
  /** CLAIMED - The incentive has been claimed by the customer */
  CLAIMED = "CLAIMED",
  /** INELIGIBLE - The incentive campaign has ended, or revoked for some other reason */
  INELIGIBLE = "INELIGIBLE",
}

/** A Investment entity is a asset that can be traded on the Cash Investment platform . */
export interface SyncInvestmentEntity {
  /** Unique identifier for an InvestmentEntity. */
  token?: string;
  /** Trading symbol, e.g.: "GOOG","AMZN","APPL". */
  symbol?: string;
  /** Proper name, e.g.: "Alphabet, Inc.", "Amazon Corp.", "Apple Computers". */
  display_name?: string;
  /** Type of entity, e.g.: CURRENCY, STOCK, ETF. */
  type?: InvestmentEntityType;
  /**
   * Web address of logo/icon.
   * Deprecated in favor of icon field below.
   *
   * @deprecated
   */
  icon_url?: string;
  /**
   * Total number of shares that are available for trading, or held by insiders (eg as RSUs).
   * Multiplied by the current share price, this constitutes the market cap of a company.
   */
  outstanding_shares?: number;
  /** Status of entity, e.g.: ACTIVE, INACTIVE. */
  status?: InvestmentEntityStatus;
  /**
   * Six digit hex representation for color associated with investment entity.
   * Includes hash e.g.: "#000000". Deprecated by `themed_color`.
   *
   * @deprecated
   */
  color?: string;
  /** End-user facing text describing the investment entity's business. */
  about_text?: string;
  /** End-user facing text describing facts about the entity. */
  about_detail_rows?: SyncInvestmentEntityDetailRow[];
  /**
   * True when a entity gets delisted and goes Over The Counter (OTC). When an entity is delisted,
   * it cannot be bought anymore and a sell order can only be `SELL_ALL`.
   */
  delisted?: boolean;
  /**
   * Color to be used with this entity.
   * DEPRECATED in favor of `themed_color`,
   * since older iOS clients contain a bug that will improperly show the dark variant in light mode.
   *
   * @deprecated
   */
  entity_color?: Color;
  /**
   * Web address of logo/icon. Dark-theme variant may be absent.
   * If missing, should fallback to deprecated icon_url field.
   */
  icon?: Image;
  /** Light- and dark-themed colors to be used with this entity. */
  themed_color?: Color;
  /**
   * Current status of the release stage of the entity, used to show Upcoming IPO treatment.
   * If missing, should fall back to RELEASED.
   */
  release_stage?: SyncInvestmentEntityReleaseStage;
}

export enum SyncInvestmentEntityReleaseStage {
  DO_NOT_USE_RELEASE_STAGE = "DO_NOT_USE_RELEASE_STAGE",
  /** PREVIEW_FOR_IPO - Entity is an upcoming IPO, users should only be able to follow */
  PREVIEW_FOR_IPO = "PREVIEW_FOR_IPO",
  /** RELEASED - Entity is fully released, users should have full functionality */
  RELEASED = "RELEASED",
}

export interface SyncInvestmentEntityDetailRow {
  /** Text shown at the left of the row. Generally the key in a key/value pair. e.g., 'CEO'. */
  title?: string;
  /**
   * Text shown at the right of the row. Generally the value in a key/value pair.
   * e.g., 'Jack Dorsey'.
   */
  detail_text?: string;
}

/**
 * A logical grouping of investment entities based on specific criteria.
 *   e.g.: "Health Sector", "Up 10% in the last year", "Pays dividends", etc.
 */
export interface SyncInvestmentCategory {
  /** String identifier for this category. */
  token?: string;
  /** Name to be displayed on this category. */
  name?: string;
  /** Glyph to display before the name of the category, if applicable. */
  prefix_icon?: SyncInvestmentCategoryPrefixIcon;
  /** Location of creative to be displayed for category. */
  image_url?: string;
  /**
   * Color to be used with this category. Deprecated by `category_color`.
   *
   * @deprecated
   */
  accent_color?: string;
  /** If the category is a subcategory, this is the token of the parent category. */
  parent_category_token?: string;
  /** Tokens of investment entities in this category. */
  investment_entity_tokens?: string[];
  /**
   * A list of filter group tokens that tell the client what to display
   * at the top of a screen, only for categories that have their own pages
   * and the top level configuration category.
   */
  ui_filter_group_tokens?: string[];
  /** Is this a category, sub-category or config-only category. */
  type?: SyncInvestmentCategoryCategoryType;
  /** Description text to be used on category detail screens. */
  description?: string;
  /** Description text to be used when category is listed in a filter. */
  filter_description?: string;
  /** Color to be used with this category. */
  category_color?: Color;
  /** Ordering of the category when compared to other categories of the same type. */
  ui_ordering?: number;
  /** Flag to enable a category to show pre-ipo stocks. */
  show_ipo_preview?: boolean;
}

export enum SyncInvestmentCategoryPrefixIcon {
  DO_NOT_USE_PREFIX_ICON = "DO_NOT_USE_PREFIX_ICON",
  UP_ARROW = "UP_ARROW",
  DOWN_ARROW = "DOWN_ARROW",
}

export enum SyncInvestmentCategoryCategoryType {
  DO_NOT_USE_CATEGORY_TYPE = "DO_NOT_USE_CATEGORY_TYPE",
  CATEGORY = "CATEGORY",
  SUBCATEGORY = "SUBCATEGORY",
  TOP_LEVEL_UI = "TOP_LEVEL_UI",
  PERFORMANCE = "PERFORMANCE",
  ADVANCED = "ADVANCED",
}

/** Grouping of categories for UI purposes. */
export interface SyncInvestmentFilterGroup {
  /** String identifier for this filter group. */
  token?: string;
  /** Name of this filter group. */
  name?: string;
  /** Category tokens for the categories included in this filter group. */
  category_tokens?: string[];
  /**
   * Advanced UI picker configurations, ordered from top-to-bottom, and with keys
   * from the outside of the category map to the inside.
   */
  subfilters?: SyncInvestmentFilterGroupSubfilter[];
  /**
   * Category Map is an extensible data structure that maps the tokens of subfilter options
   * to the categories that they represent in combination. e.g.:
   * CategoryMapNode{
   *   option_token: "10_UP",
   *   category_token: null,
   *   sub_nodes: [
   *     CategoryMapNode{option_token: "YEAR", category_token: "10_UP_YEAR_TOKEN", sub_nodes: null},
   *     CategoryMapNode{option_token: "90D", category_token: "10_UP_90D_TOKEN", sub_nodes: null},
   *     ...
   *   ]
   * }
   */
  category_map?: SyncInvestmentFilterGroupCategoryMapNode[];
  /** If true, multiple categories can be selected at once. */
  can_select_multiple_categories?: boolean;
  /** If there are multiple categories selected and this is filled, use this name. */
  name_plural?: string;
  /**
   * If the FilterGroup is a list of categories, specify how to join results when
   * multiple categories are selected.
   */
  joinType?: SyncInvestmentFilterGroupJoinType;
}

export enum SyncInvestmentFilterGroupJoinType {
  DO_NOT_USE_JOIN_TYPE = "DO_NOT_USE_JOIN_TYPE",
  INTERSECTION = "INTERSECTION",
  UNION = "UNION",
}

/** When a more advanced Filter UI is required, subfilters allow that segmentation. */
export interface SyncInvestmentFilterGroupSubfilter {
  /** String identfier for this subfilter. */
  token?: string;
  /** Subfilter type. e.g.: "INCREMENTAL", "SELECTION" */
  type?: SyncInvestmentFilterGroupSubfilterSubfilterType;
  /** Name for this subfilter. */
  name?: string;
  /** Index where the placeholder category should appear, if applicable. */
  placeholder_index?: number;
  /**
   * Text to appear for the placeholder category's label.
   *   e.g.: "Select Amount" for the Performance subfilter.
   */
  placeholder_text?: string;
  /**
   * The available options for this subfilter. The order of the list
   * is the order they will show in the UI.
   */
  options?: SyncInvestmentFilterGroupSubfilterOption[];
}

export enum SyncInvestmentFilterGroupSubfilterSubfilterType {
  DO_NOT_USE_SUBFILTER_TYPE = "DO_NOT_USE_SUBFILTER_TYPE",
  INCREMENTAL = "INCREMENTAL",
  SELECTION = "SELECTION",
}

/** Pickable item for a given subfilter. */
export interface SyncInvestmentFilterGroupSubfilterOption {
  /** String identifier for this option */
  token?: string;
  /** Name for this option. */
  name?: string;
  /** Glyph to be prepended to the name of the option, if applicable. */
  glyph?: SyncInvestmentFilterGroupSubfilterOptionOptionGlyph;
}

/** Available glyphs for display in the subfilter options. */
export enum SyncInvestmentFilterGroupSubfilterOptionOptionGlyph {
  DO_NOT_USE_OPTION_GLYPH = "DO_NOT_USE_OPTION_GLYPH",
  UP_ARROW = "UP_ARROW",
  DOWN_ARROW = "DOWN_ARROW",
}

/** Extensible mapping of subfilter options to categories. */
export interface SyncInvestmentFilterGroupCategoryMapNode {
  /** The key of this node, corresponds to an option token. */
  option_token?: string;
  /** Token for category that this node maps to, if this is a leaf node, otherwise null. */
  category_token?: string;
  /**
   * If this is not a leaf node, this is the mapping of nodes representing following
   * subfilters from this one, otherwise null.
   */
  sub_nodes?: SyncInvestmentFilterGroupCategoryMapNode[];
}

/**
 * The ranking order of investment entities for use in Investing Search & Category detail views.
 * Generated daily based on trade volume.
 */
export interface SyncInvestmentEntityRanking {
  /** What the ranking should be used for. */
  ranking_type?: SyncInvestmentEntityRankingRankingType;
  /** The list of investment entities in their ranked order. */
  investment_entity_tokens?: string[];
}

export enum SyncInvestmentEntityRankingRankingType {
  DO_NOT_USE_RANKING_TYPE = "DO_NOT_USE_RANKING_TYPE",
  SEARCH_AND_CATEGORY_VIEWS = "SEARCH_AND_CATEGORY_VIEWS",
}

/** A receipt that corresponds to a customer card transaction */
export interface SyncReceiptEntity {
  receipt?: UiItemizedReceipt;
}

/** An invitation to use Cash App. */
export interface SyncInvitation {
  /** Set to null if the invitation has expired. */
  invitation?: UiInvitation;
}

/** A value that is a part of a customer's profile. */
export interface SyncValue {
  type?: SyncValueType;
  instrument?: Instrument | undefined;
  atm_picker?:
    | ATMPicker
    | undefined;
  /**
   * Sync entity ID is "BS_B$_C_12345678" for instrument token "B$_C_12345678".
   *
   * The triple prefix is unfortunate. Instruments for stored value balances have a prefix "B$_"
   * followed by the initial customer, "C_12345678" which has its own prefix. We namespace it
   * further "BS_" to avoid causing a conflict between balances and their instruments.
   */
  balance_snapshot?:
    | BalanceSnapshot
    | undefined;
  /** Sync entity ID is "DDA_PRIMARY" for any customer. */
  dda?:
    | UiDda
    | undefined;
  /** Sync Entity ID is "ACCESS_PRIMARY" for any customer. */
  access?:
    | UiAccess
    | undefined;
  /** Sync Entity ID is "ADDRESS_PRIMARY" for any customer. */
  address?:
    | UiAddress
    | undefined;
  /** Sync Entity ID is "SNL_PRIMARY" for any customer. */
  status_and_limits?:
    | UiStatusAndLimits
    | undefined;
  /** Sync Entity ID is "APM_PRIMARY" for any customer. */
  app_messages?:
    | UiAppMesssages
    | undefined;
  /** Sync Entity ID is "CC_C_123" for customer "C_123". */
  core_customer?:
    | UiCoreCustomer
    | undefined;
  /** Sync Entity ID is "EXP_PRIMARY" for any customer. */
  experiments?:
    | UiExperiments
    | undefined;
  /** Sync Entity ID is "CARD_PRIMARY" for any customer. */
  issued_card?:
    | UiIssuedCard
    | undefined;
  /** Sync Entity ID is "MRKT_PRIMARY" for any customer. */
  marketing?:
    | UiMarketing
    | undefined;
  /** Sync Entity ID is "P2PSET_PRIMARY" for any customer. */
  p2p_settings?:
    | UiP2pSettings
    | undefined;
  /** Sync Entity ID is "SHPAY_PRIMARY" for any customer. */
  scheduled_payments?:
    | UiScheduledPayments
    | undefined;
  /** Sync Entity ID is "TAX_PRIMARY" for any customer. */
  tax?:
    | UiTax
    | undefined;
  /** Sync Entity ID is "PP_PRIMARY" for any customer. */
  public_profile?:
    | UiPublicProfile
    | undefined;
  /** Sync Entity ID is "ALIAS_12345" for any customer, where 12345 is version of an alias. */
  profile_alias?:
    | ProfileAlias
    | undefined;
  /** Sync Entity ID is "LPRF_PRIMARY" for any customer. */
  loyalty_profile?:
    | LoyaltyProfile
    | undefined;
  /** Sync Entity ID is "CDPRF_PRIMARY" for any customer. */
  check_deposit_profile?:
    | CheckDepositProfile
    | undefined;
  /** Sync Entity ID is "INVSET_PRIMARY" for any customer. */
  investment_notification_settings?:
    | NotificationsSettings
    | undefined;
  /** Sync Entity ID is "PD_PRIMARY" for any customer. */
  profile_details?:
    | ProfileDetails
    | undefined;
  /** Sync Entity ID is "WALLET_B$_C_123" for stored value B$_C_123. */
  wallet?:
    | Wallet
    | undefined;
  /** Sync Entity ID is "BANKING_TAB" for any customer. */
  banking_tab?:
    | BankingTab
    | undefined;
  /** Sync Entity ID is "JC_PRIMARY" for any customer. */
  jurisdiction_config?:
    | UiJurisdictionConfig
    | undefined;
  /** Sync Entity ID is "CRYPTO_PRIMARY" for any customer. */
  cryptocurrency?:
    | UiCryptoCurrency
    | undefined;
  /** Sync Entity ID is "EXCHANGE_PRIMARY" for any customer. */
  exchange_data?:
    | UiExchangeData
    | undefined;
  /** Sync Entity ID is "NPRF_abc123", where abc123 is a hash of an alias. */
  notification_preference?:
    | UiNotificationPreference
    | undefined;
  /** Sync Entity ID is "???" for any customer. */
  data_privacy_settings?:
    | DataPrivacySettings
    | undefined;
  /** Sync Entity ID is "PASSWORD_INFO" for any customer */
  password_info?:
    | PasswordInfo
    | undefined;
  /** Sync Entity ID is "OTP_INFO" for any customer */
  otp_info?:
    | OTPInfo
    | undefined;
  /**
   * Sync Entity ID is "FAMILY_AA_C_123" where AA is an abbreviation for the family
   * account type (e.g. SA) and C_123 is the customer token of other customer in this relationship
   * (not the customer that the SyncEntity is being published to)
   */
  family_account?:
    | UiFamilyAccount
    | undefined;
  /**
   * Sync Entity ID is "INVAUTO_<use_case>", where use cases are defined as:
   *   RU => Round-ups (e.g.- "INVAUTO_RU")
   */
  investing_automation?:
    | UiInvestingAutomation
    | undefined;
  /** Sync Entity ID is "LENDING_INFO" for any customer */
  lending_info?:
    | LendingInfo
    | undefined;
  /** Sync Entity ID is "TC_PRIMARY" for any customer */
  trusted_contact?:
    | SyncTrustedContact
    | undefined;
  /** Sync Entity ID is "IDV_STATE" for any customer */
  idv_state?:
    | UiIdvState
    | undefined;
  /** Sync Entity ID is "CRY_PROFILE" for any customer. */
  cryptocurrencyProfile?:
    | CryptocurrencyProfile
    | undefined;
  /** Sync Entity ID is "INVAUTO_NOTIFICATION" for any customer. */
  invest_automator_notification_settings?:
    | NotificationsSettings
    | undefined;
  /** Sync Entity ID is "FAVORITE" for any customer. */
  favorite?:
    | Favorite
    | undefined;
  /** Sync Entity ID is "PIGGYBANK_SAVINGS_CONFIG_SYNC_VALUE" for any customer. */
  savings_config?:
    | SavingsConfig
    | undefined;
  /** Sync Entity ID is "PIGGYBANK_SAVINGS_HOME_SYNC_VALUE" for any customer. */
  savings_home?:
    | SavingsHome
    | undefined;
  /** Sync Entity ID is "INVSTATE_PORTFOLIO_C_123" for customer "C_123" */
  invest_portfolio_state?:
    | PortfolioState
    | undefined;
  /** Sync Entity ID is "INVSTATE_TRADING_C_123" for customer "C_123" */
  invest_trading_state?:
    | TradingState
    | undefined;
  /** Sync Entity ID is "PIGGYBANK_SAVINGS_FOLDER_B$_svngs_123" for customer with savings balance token B$_svngs_123 */
  savings_folder?:
    | SavingsFolder
    | undefined;
  /** Sync Entity ID is "TODO(Growth Optimization)" for any customer. */
  tigers?:
    | Tigers
    | undefined;
  /** Sync Entity ID for Wires account information. */
  wires_account_info?:
    | WiresAccountInfo
    | undefined;
  /** Sync Entity ID for Tax Upgrade info. */
  tax_upgrade?:
    | UiTaxUpgrade
    | undefined;
  /** Sync Entity ID for Tax Efile info. */
  efile_info?:
    | UiEfileInfo
    | undefined;
  /** Sync Entity ID for cash limits. */
  cash_limits?:
    | UiCashLimits
    | undefined;
  /** Sync Entity ID is "TODO(Growth Optimization)" for any customer. */
  lions?:
    | Lions
    | undefined;
  /** Sync Entity ID is "DEVICE_appToken" for all customers */
  device_info?:
    | DeviceInfo
    | undefined;
  /** Sync Entity ID for limits inline message. */
  limits_pagelet_inline_message?:
    | LimitsPageletInlineMessage
    | undefined;
  /** Sync Entity ID is "SCHEMA_VERSIONS" on global value */
  schema_versions?:
    | SyncValueSchemaVersions
    | undefined;
  /** Sync Entity ID is "TODO(Growth Optimization)" for any customer. */
  cats?:
    | Cats
    | undefined;
  /** Sync Entity ID is "INS_LINK_OPTION_IL_12345 for any customer, where IL_12345 is an InstrumentLinkToken */
  instrument_linking_option?:
    | InstrumentLinkingOption
    | undefined;
  /** Sync Entity ID is "TODO(Growth Optimization)" for any customer. */
  rabbits?:
    | Rabbits
    | undefined;
  /** Sync Entity ID is OVERDRAFT_STATUS. */
  overdraft_status?:
    | OverdraftStatus
    | undefined;
  /** Sync Entity ID is OVERDRAFT_USAGE. */
  overdraft_usage?:
    | OverdraftUsage
    | undefined;
  /** Sync Entity ID is "INSLINKOPTIONS_C_1234567" for customer C_1234567 */
  instrument_linking_options?:
    | InstrumentLinkingOptions
    | undefined;
  /** Sync Entity ID for identity hub state. */
  identity_hub_state?:
    | IdentityHubState
    | undefined;
  /** Sync Entity ID is "APP_LOCK" for any customer. */
  app_lock?:
    | UiAppLock
    | undefined;
  /** Sync Entity ID is "SP_CRYPTO_AUTHZ" for any customer. */
  sponsorship_crypto_authorization?:
    | SponsorshipCryptoAuthorization
    | undefined;
  /** Sync Entity ID is "OFFER_CUSTOMER_PREFERENCE" for any customer. */
  offer_customer_preference?:
    | OfferCustomerPreference
    | undefined;
  /** Sync Entity ID is "PASSKEY_OPTIONS" for any customer. */
  passkey_options?:
    | PasskeyOptions
    | undefined;
  /** Sync Entity ID is "PAYCHECKS_ALLOCATION_DISTRIBUTION" for any customer */
  paycheck_allocation_distribution?:
    | AllocationDistribution
    | undefined;
  /** Sync Entity ID is "PAYCHECKS_UI_CONFIGURATION" for any customer */
  paychecks_ui_configuration?:
    | UiConfiguration
    | undefined;
  /** Sync Entity ID is "PAYCHECKS_UI_STATE" for any customer */
  paychecks_ui_state?:
    | UiState
    | undefined;
  /** Sync Entity ID is "PAPER_CASH_DEPOSIT_ELIGIBILITY" for any customer */
  paper_cash_deposit_eligibility?:
    | PaperCashDepositEligibility
    | undefined;
  /** Sync Entity ID is "CHECK_DEPOSIT_ELIGIBILITY" for any customer */
  check_deposit_eligibility?:
    | CheckDepositEligibility
    | undefined;
  /** Sync Entity ID is "WIRES_ELIGIBILITY_STATE" for any customer */
  wires_eligibility_state?:
    | WiresEligibilityState
    | undefined;
  /** Sync Entity ID is "BC_<customerToken>" for any customer */
  borrow_config?:
    | BorrowConfig
    | undefined;
  /** Sync Entity ID is "GBC_DATA" for any customer */
  global_borrow_config?:
    | GlobalBorrowConfig
    | undefined;
  /** TODO: add sync entity ID */
  borrow_applet_credit_limit_borrow_button_tile?:
    | BorrowAppletCreditLimitAndBorrowButtonTile
    | undefined;
  /** Sync Entity ID is "C4B_PROFILE" for any customer */
  c4b_profile_data?:
    | BusinessProfileData
    | undefined;
  /** Sync Entity ID is "REACTIONS_AVAILABLE" for any customer */
  reactions_available?:
    | AvailableReactions
    | undefined;
  /** TODO: add sync entity ID */
  borrow_applet_payment_timeline_tile?:
    | BorrowAppletPaymentTimelineTile
    | undefined;
  /** TODO: add sync entity ID */
  borrow_applet_bulletins_tile?:
    | BorrowAppletBulletinsTile
    | undefined;
  /** Sync Entity ID is "INTL_COUNTRIES_CONFIG_<N>" for any customer */
  international_payments_country_config?:
    | InternationalPaymentsCountrySelectionSyncValue
    | undefined;
  /** TODO: add sync entity ID */
  borrow_applet_loan_history_tile?: BorrowAppletLoanHistoryTile | undefined;
}

/** A customer's preference for a specific recurring schedule. */
export interface SyncRecurringPreference {
  /** Preference for this specific recurring schedule. */
  preference?: ScheduledTransactionPreference;
}

/** A customer's preference for crypto payroll conversion. */
export interface SyncCryptoPayrollPreference {
  /** Preference for this specific conversion. */
  preference?: CryptoPayrollPreference;
}

/** A customer's atm picker * / */
export interface ATMPicker {
  options?: Money[];
}

/** A receipt that corresponds to a check deposit */
export interface SyncCheckDepositReceiptEntity {
  receipt?: UiCheckDeposit;
}

/**
 * A wrapped SyncEntity, encrypted with a clients public key.
 * Clients provide a tink keyset holding the public portion of an ECIES key.
 */
export interface EncryptedSyncEntity {
  /** Raw cipher bytes, this is the wrapped SyncEntity. */
  encrypted_sync_entity?: Uint8Array;
  /** The nested SyncEntity's type. */
  type?: SyncEntityType;
  /** If the nested SyncEntity is a SyncValue, the SyncValueType is populated. */
  sync_value_type?: SyncValueType;
}

/**
 * Notification settings will usually be sent via INVESTMENT_NOTIFICATION_SETTINGS sync value,
 * but for users who haven't taken action yet, these defaults will populate the client.
 */
export interface InvestDefaultNotificationSettings {
  /** Settings for invest notifications for user who hasn't purchased/followed bitcoin/stock. */
  settings?: NotificationSettingsOption[];
  /** Configuration of messages sent to client, this is to indicate the notification channel ids. */
  message_settings?: NotificationSettingsMessageConfig[];
}

/**
 * Display model for a single screen.
 * http://go/treehouse
 */
export interface CashHouseScreen {
  /** kotlinx.serialization of a com.squareup.cash.house.core.Screen. */
  screen_json?: string;
}

export interface SyncValueSchemaVersion {
  /** required */
  type?: SyncValueType;
  /** required */
  version?: number;
}

/**
 * Protobuf does not allow maps with enum keys, see: https://developers.google.com/protocol-buffers/docs/proto#maps
 * Instead of using a map or sacrificing type safety we can use a repeated custom type to get a
 */
export interface SyncValueSchemaVersions {
  /** map-like list where the SyncValueType is the "key". Must not contain duplicates of SyncValueType */
  versions?: SyncValueSchemaVersion[];
}

export interface InstrumentLinkingOptions {
  instrument_linking_option?: InstrumentLinkingOption[];
}

/**
 * OOTB badging support
 * This is owned and maintained by ClientSync. No other service should be setting this field.
 * This is needed in SyncEntity since that is what ClientSync sends down to the client. Unfortunately,
 * SyncEntity is also what services use to write to ClientSync making this field accessible to callers
 * but techincally off-limits.
 */
export interface BadgingInfo {
  /** Represents if the entity is badged. */
  is_badged?: boolean;
  /** Badging version would be used by clients to update their local state. */
  badging_version?: number;
}
